import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Theme mode types
 */
export type ThemeMode = 'light' | 'dark' | 'system';

/**
 * Service to manage dark/light theme switching
 */
@Injectable({
	providedIn: 'root',
})
export class ThemeModeService {
	private readonly THEME_STORAGE_KEY = 'spyderflow-theme-mode';
	private themeModeSubject: BehaviorSubject<ThemeMode>;
	public themeMode$: Observable<ThemeMode>;

	constructor() {
		// Initialize with stored preference or default to 'light'
		const storedMode = this.getStoredThemeMode();
		this.themeModeSubject = new BehaviorSubject<ThemeMode>(storedMode);
		this.themeMode$ = this.themeModeSubject.asObservable();

		// Apply the initial theme
		this.applyThemeMode(storedMode);
	}

	/**
	 * Get the current theme mode
	 */
	getCurrentThemeMode(): ThemeMode {
		return this.themeModeSubject.value;
	}

	/**
	 * Set the theme mode
	 * @param mode The theme mode to set
	 */
	setThemeMode(mode: ThemeMode): void {
		this.themeModeSubject.next(mode);
		this.storeThemeMode(mode);
		this.applyThemeMode(mode);
	}

	/**
	 * Toggle between light and dark mode
	 */
	toggleTheme(): void {
		const currentMode = this.getCurrentThemeMode();
		const newMode = currentMode === 'light' ? 'dark' : 'light';
		this.setThemeMode(newMode);
	}

	/**
	 * Check if the current theme is dark
	 */
	isDarkMode(): boolean {
		const mode = this.getCurrentThemeMode();
		if (mode === 'system') {
			return this.isSystemDarkMode();
		}
		return mode === 'dark';
	}

	/**
	 * Check if the system prefers dark mode
	 */
	private isSystemDarkMode(): boolean {
		return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
	}

	/**
	 * Apply the theme mode to the document
	 * @param mode The theme mode to apply
	 */
	private applyThemeMode(mode: ThemeMode): void {
		const html = document.documentElement;
		const body = document.body;
		const isDark = mode === 'dark' || (mode === 'system' && this.isSystemDarkMode());

		if (isDark) {
			html.classList.add('dark-theme', 'p-dark');
			body.classList.add('dark-theme', 'p-dark');
			body.classList.remove('light-theme');
		} else {
			html.classList.remove('dark-theme', 'p-dark');
			body.classList.add('light-theme');
			body.classList.remove('dark-theme', 'p-dark');
		}

		// Update data attribute for reference
		document.documentElement.setAttribute('data-theme', isDark ? 'dark' : 'light');
		console.log('[ThemeModeService] Applied theme mode:', mode, 'isDark:', isDark);
	}

	/**
	 * Get the stored theme mode from localStorage
	 */
	private getStoredThemeMode(): ThemeMode {
		try {
			const stored = localStorage.getItem(this.THEME_STORAGE_KEY);
			if (stored && ['light', 'dark', 'system'].includes(stored)) {
				return stored as ThemeMode;
			}
		} catch (error) {
			console.warn('[ThemeModeService] Error reading theme mode from localStorage:', error);
		}
		return 'dark'; // Default to dark mode
	}

	/**
	 * Store the theme mode in localStorage
	 * @param mode The theme mode to store
	 */
	private storeThemeMode(mode: ThemeMode): void {
		try {
			localStorage.setItem(this.THEME_STORAGE_KEY, mode);
		} catch (error) {
			console.warn('[ThemeModeService] Error storing theme mode to localStorage:', error);
		}
	}

	/**
	 * Listen to system theme changes
	 * Call this method in the app initialization to enable auto-switching with system theme
	 */
	listenToSystemThemeChanges(): void {
		if (window.matchMedia) {
			const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
			mediaQuery.addEventListener('change', _e => {
				if (this.getCurrentThemeMode() === 'system') {
					this.applyThemeMode('system');
				}
			});
		}
	}
}
